import { Box, Button, IconButton } from '@mui/material';

import AppStoreBadge from 'src/resources/svg/AppStoreBadge.svg';
import GooglePlayStoreBadge from 'src/resources/svg/GooglePlayStoreBadge.svg';
import IPlatform from '@orcha/types/platform';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { QRCodeSVG } from 'qrcode.react';
import { logEventAnalytic } from 'src/utilities/logAnalytics';
import theme from 'src/themes';
import useTranslation from 'next-translate/useTranslation';
import { useSiteContext } from 'src/store';
import useStorage from 'src/hooks/useStorage';
import { DownloadDialogAppData } from './DownloadDialog.types';

type DownloadDialogActionsProps = {
  appData: DownloadDialogAppData;
};

export const DownloadDialogActions = ({
  appData,
}: DownloadDialogActionsProps) => {
  const { appId, name, platforms, appStatus } = appData;
  const { url } = useSiteContext();
  const { tenantId, packageId } = useSiteContext();

  const tracking = useStorage().getItem('trackingId', 'local') as
    | { trackingId: string }
    | undefined;
  const fullDomain =
    url?.subDomainName && url?.domainName
      ? `${url.subDomainName}.${url.domainName}`
      : '';
  /**
Creates a download tracking link with the necessary query parameters.
@param {string} [appDownloadLink] - The URL to the app download.
@returns {string} - The complete URL with tracking parameters.
*/
  const getRedirectLink = (
    appDownloadLink: string,
    storeFront: 'ios' | 'android' | 'web' | 'custom' | undefined
  ): string => {
    if (!fullDomain) return appDownloadLink;

    const queryParams = new URLSearchParams({
      tenantId: tenantId || '',
      packageId: packageId || '',
      appId,
      trackingId: tracking?.trackingId ?? '',
      storeFront: storeFront || 'ios',
    });

    return `https://${fullDomain}/api/get-redirect?${queryParams}`;
  };

  const showSupplierUrlAction =
    appStatus?.status !== 'Commissionable' &&
    !appStatus?.useNativeUrl &&
    !!appStatus?.supplierUrl &&
    appStatus.supplierUrl.length > 0;

  const { t } = useTranslation('appCommon');

  const handleStoreClick = (platform: IPlatform['platformName']) => {
    logEventAnalytic({
      category: 'Download',
      action: 'Downloaded from download modal - microsite',
      label: `${appId} - ${name} - ${platform}`,
    });
  };

  const GetLink = (linkUrl: string | undefined) => {
    if (!linkUrl) return '#';
    if (linkUrl.startsWith('http')) {
      return linkUrl;
    }
    return `//${linkUrl}`;
  };

  return (
    <>
      {showSupplierUrlAction && (
        <Box sx={{ display: 'grid', gap: theme.spacing(2) }}>
          <Box
            data-testid="download-commissioned-app-qrcode-image-container"
            sx={{ justifySelf: 'center' }}
          >
            <QRCodeSVG
              value={getRedirectLink(appStatus?.supplierUrl ?? '', 'custom')}
            />
          </Box>
          <Button
            data-testid="download-supplier-url-button"
            component="a"
            aria-label={t(
              'appCommon:downloadDialog.commissionedAppButtonTitle'
            )?.toString()}
            title={t(
              'appCommon:downloadDialog.commissionedAppButtonTitle'
            )?.toString()}
            href={GetLink(
              getRedirectLink(appStatus?.supplierUrl ?? '', 'custom')
            )}
            target="_blank"
            sx={{ justifySelf: 'center' }}
            variant="contained"
          >
            {t('appCommon:downloadDialog.commissionedAppButtonLabel')}
          </Button>
        </Box>
      )}
      {!showSupplierUrlAction &&
        platforms?.map((platform) => {
          if (platform?.downloadLink) {
            return (
              <Box
                key={platform?.platformName}
                sx={{ display: 'grid', gap: '15px' }}
                data-testid="download-dialog-platform-item-container"
              >
                <Box sx={{ justifySelf: 'center' }}>
                  <QRCodeSVG
                    value={GetLink(
                      getRedirectLink(
                        platform.downloadLink,
                        platform.platformName
                      )
                    )}
                  />
                </Box>

                {platform?.platformName === 'web' ? (
                  <Button
                    component="a"
                    href={GetLink(
                      getRedirectLink(platform.downloadLink, 'web')
                    )}
                    target="_blank"
                    aria-label={t(
                      'downloadDialog.webAppButtonTitle'
                    )?.toString()}
                    data-testid="download-web-app-button"
                    onClick={() => handleStoreClick(platform.platformName)}
                    title={t('downloadDialog.webAppButtonTitle')}
                    sx={{ justifySelf: 'center' }}
                    variant="outlined"
                    endIcon={<OpenInNew />}
                  >
                    {t('downloadDialog.webAppButtonLabel')}
                  </Button>
                ) : (
                  <Box
                    sx={{
                      justifySelf: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <IconButton
                      component="a"
                      rel="noopener noreferrer"
                      target="_blank"
                      href={GetLink(
                        getRedirectLink(
                          platform?.downloadLink,
                          platform.platformName
                        )
                      )}
                      onClick={() => handleStoreClick(platform.platformName)}
                    >
                      {platform?.platformName === 'ios' && (
                        <AppStoreBadge
                          data-testid="download-app-store-button"
                          height="40px"
                          alt={t('downloadDialog.appStoreBadgeAltText')}
                        />
                      )}
                      {platform?.platformName === 'android' && (
                        <GooglePlayStoreBadge
                          data-testid="download-google-play-button"
                          height="40px"
                          alt={t('downloadDialog.googlePlayStoreBadgeAltText')}
                        />
                      )}
                    </IconButton>
                  </Box>
                )}
              </Box>
            );
          }
          return <></>;
        })}
    </>
  );
};

export default DownloadDialogActions;
